// @flow
import styled from '@emotion/styled';
import { color, SPACING, typography } from '@tablekit/theme';
import Icon from '@tablekit/icon';
import Button from '@tablekit/button';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import Link from 'components/Link';
import TruncatedText from 'components/TruncatedText';
import { getPageContext } from 'components/PageContext';
import { MOBILE_BREAKPOINT } from 'utils/theme';
import { safeLink } from 'utils/safeLink';
import type { SiblingLinkType } from './types';
import { PostBody } from './styled';

type PropsType = {
  next?: SiblingLinkType,
  prev?: SiblingLinkType
};

const Wrapper = styled(PostBody)`
  display: flex;
  flex-direction: row;
  flex: 1 1 100%;
  padding-bottom: 0;

  @media ${MOBILE_BREAKPOINT} {
    flex-direction: column;
    margin: ${SPACING.L6} 0;
  }
`;

const NextPrevLink = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -${SPACING.L6};
`;

const PrevLink = styled(NextPrevLink)`
  text-align: left;
  align-items: flex-start;
`;

const NextLink = styled(NextPrevLink)`
  text-align: right;
  align-items: flex-end;
`;

const NextPrevExpander = styled.div`
  display: flex;
  flex: 1 1 100%;
`;

const DirectionTitle = styled(Button)`
  margin-bottom: ${SPACING.L2};
  text-transform: uppercase;

  .fa-arrow-left {
    margin-right: ${SPACING.L3};
  }
  .fa-arrow-right {
    margin-left: ${SPACING.L3};
  }
`;

const DirectionText = styled.div`
  color: ${color('text')};
  max-width: ${SPACING.L10};
  width: ${SPACING.L10};
  ${typography('L3')};
  margin: 0 ${SPACING.L6};

  @media ${MOBILE_BREAKPOINT} {
    ${typography('L1')};
    margin: 0 ${SPACING.L4};
  }
`;

const SiblingLinks = ({ next, prev }: PropsType) => {
  if (!next && !prev) return null;
  const { blogConfig } = getPageContext();
  return (
    <Wrapper>
      {prev && (
        <PrevLink>
          <DirectionTitle
            to={safeLink(prev.slug)}
            component={Link}
            appearance="subtle"
            size={{ [MOBILE_BREAKPOINT]: 'small', default: 'large' }}
            iconBefore={<Icon icon={faArrowLeft} />}
          >
            {blogConfig.prev_post_text}
          </DirectionTitle>
          <TruncatedText
            Component={DirectionText}
            typography={{ default: { lines: 2, level: 'L3' } }}
            text={prev.title}
          />
        </PrevLink>
      )}
      <NextPrevExpander />
      {next && (
        <NextLink>
          <DirectionTitle
            component={Link}
            to={safeLink(next.slug)}
            appearance="subtle"
            size={{ [MOBILE_BREAKPOINT]: 'small', default: 'large' }}
            iconAfter={<Icon icon={faArrowRight} />}
          >
            {blogConfig.next_post_text}
          </DirectionTitle>
          <TruncatedText
            Component={DirectionText}
            typography={{ default: { lines: 2, level: 'L3' } }}
            text={next.title}
          />
        </NextLink>
      )}
    </Wrapper>
  );
};

export default SiblingLinks;
